import { HttpParams } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  IELAsyncAutocompleteElement,
  SELECTION_MODE,
} from '@el-async-autocomplete';
import {
  DataListTableActionElement,
  DataListTableChipDataElement,
  DataListTableDataElement,
  DataListTableElement,
  DLT_DATA_TYPES,
} from '@el-data-list-table';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import {
  AttachmentStatus,
  CHIP_BACKGROUND_COLORS,
  FILE_TYPES,
  MESSAGE_PRIORITY,
  MESSAGE_STATUS,
  TO_RULE_TYPE,
} from '@shared/constants';
import { MessageGroupResponseDTO } from '@shared/dtos';
import {
  IAttachmentDocument,
  IConnection,
  IIdentity,
  IMessage,
  IPaginationParamsQuery,
  IRole,
  IUploadFileToDMS,
} from '@shared/interfaces';
import { generateFileTypeRegex } from '@shared/utils';

import { DateTimeFormatService } from '../../../../services/date-time-format.service';
import { FilesService } from '../../../../services/files.service';
import {
  SnackbarService,
  SUCCESS_TYPES,
} from '../../../../services/snackbar.service';
import Base64UploaderPlugin from '../../../../utils/Base64Upload';
import { UserInfoResponseDTO } from '../../../auth/services';
import { MessageRuleService } from '../../../setup/messages/services/message-rules.service';
import {
  CONFIRMATION_TYPES,
  GetUserConfirmationService,
} from '../../../shared/services/get-user-confirmation/service';

const imageRegex = /image\/*/;

interface DropdownElement {
  value: string;
  [key: string]: any;
}

export interface ReceivingUser {
  name: string;
  identity_id: string;
  isGroup: boolean;
}

const ACTIONS = {
  DOWNLOAD: 'DOWNLOAD',
  DELETE: 'DELETE',
};

export interface IFilterData extends IPaginationParamsQuery {
  type: 'typing' | 'scroll';
  event: string;
}

export enum TYPES {
  STATUS = 'Status',
}

export enum DOCUMENT_DATA_STATUS {
  PENDING_DELETE = 'Pending Delete',
}

@Component({
  selector: 'app-create-thread',
  templateUrl: './create-thread.component.html',
  styleUrls: ['./create-thread.component.scss'],
})
export class DialogCreateThreadComponent {
  allowedTypesNames =
    '*.docx/ *.xlsx/ *.jpeg/ *.png/ *.mp4/ *.avi/ *.wmv/ *.mkv/ *.pdf/ *.eml/ and *.csv';

  allowedTypesRegex = generateFileTypeRegex([
    FILE_TYPES.PDF,
    FILE_TYPES.IMAGE,
    FILE_TYPES.VIDEO,
    FILE_TYPES.BINARY,
    FILE_TYPES.DOCS,
    FILE_TYPES.EXCEL,
    FILE_TYPES.CSV,
    FILE_TYPES.EML,
  ]);

  maxSize = 10;
  uploadSub: Subscription;
  selectedFile: File;

  createThreadForm = new UntypedFormGroup({
    subject: new UntypedFormControl(null, [Validators.required]),
    priority: new UntypedFormControl(MESSAGE_PRIORITY.LOW),
    message: new UntypedFormControl(null, Validators.required),
  });

  isLoading = false;
  hasBodyError = false;

  // TODO:@oshen missing translations
  title = 'Upload Files Related to the Message';

  documents: IAttachmentDocument[] = [];
  documentsDataList: DataListTableElement[] = [];

  isFilesEmpty = false;
  usersListForAutoComplete: DropdownElement[] = [];
  usersListForAsyncAutoComplete: IELAsyncAutocompleteElement[] = [];
  totalUsers = 0;

  loggedUserInfo: UserInfoResponseDTO;

  editor = ClassicEditor;
  editorConfig = {
    extraPlugins: [Base64UploaderPlugin],
    removePlugins: ['MediaEmbed'],
    basicEntities: true,
    autoParagraph: false,
    disallowedContent: true,
  };

  ruleType: TO_RULE_TYPE = TO_RULE_TYPE.TO_IDENTITY;
  TO_RULE_TYPE = TO_RULE_TYPE;

  MESSAGE_PRIORITY = MESSAGE_PRIORITY;
  priorityType: MESSAGE_PRIORITY = MESSAGE_PRIORITY.LOW;

  SELECTION_MODE = SELECTION_MODE;
  selectedIdentityReceiver: IELAsyncAutocompleteElement;
  selectedRoleReceiver: IELAsyncAutocompleteElement;
  selectedGroupReceiver: IELAsyncAutocompleteElement;

  isReceiverMarkAsTouched = false;
  isShowIdentityError = false;
  isShowRoleError = false;
  isShowGroupError = false;

  toBeUploadedData: IUploadFileToDMS<File>[] = [];
  toBeUploadedDocuments: IAttachmentDocument[] = [];
  toBeDeletedData: IAttachmentDocument[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogCreateThreadComponent>,
    private dateTimeFormatService: DateTimeFormatService,
    private translate: TranslateService,
    private snackbar: SnackbarService,
    private getUserConfirmation: GetUserConfirmationService,
    private filesService: FilesService,
    private messageRuleService: MessageRuleService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      loggedUser: UserInfoResponseDTO;
    }
  ) {
    this.loggedUserInfo = data.loggedUser;
  }

  onTypeSelected(value: TO_RULE_TYPE) {
    this.ruleType = value;
    switch (value) {
      case TO_RULE_TYPE.TO_IDENTITY:
        if (this.selectedIdentityReceiver?.key) {
          setTimeout(() => {
            this.isShowIdentityError = false;
          }, 0);
        } else {
          setTimeout(() => {
            this.isShowIdentityError = true;
          }, 0);
        }
        break;

      case TO_RULE_TYPE.TO_ROLE:
        if (this.selectedRoleReceiver?.key) {
          setTimeout(() => {
            this.isShowIdentityError = false;
          }, 0);
        } else {
          setTimeout(() => {
            this.isShowIdentityError = true;
          }, 0);
        }
        break;

      case TO_RULE_TYPE.TO_GROUP:
        if (this.selectedGroupReceiver?.key) {
          setTimeout(() => {
            this.isShowGroupError = false;
          }, 0);
        } else {
          setTimeout(() => {
            this.isShowGroupError = true;
          }, 0);
        }
        break;

      default:
        break;
    }
  }

  onPrioritySelected(value: MESSAGE_PRIORITY) {
    this.priorityType = value;
    this.createThreadForm.controls.priority.setValue(value);
  }

  editorChange() {
    this.hasBodyError = false;
  }

  triggerFileSelected(file: File) {
    if (
      file.type.match(imageRegex) ||
      file.type.match(this.allowedTypesRegex)
    ) {
      const document: IAttachmentDocument = {
        name: file.name,
        uploaded_date: new Date(),
        type: 'unknown',
        status: AttachmentStatus.UPLOADING,
      };

      this.saveDocument(document, file);
    }
  }

  saveDocument(document: IAttachmentDocument, file: File) {
    this.createThreadForm.markAllAsTouched();
    this.createThreadForm.markAsDirty();
    this.createThreadForm.updateValueAndValidity();

    const index = this.documentsDataList.length;

    const entity: IUploadFileToDMS<File> = {
      is_public: false,
      is_system: true,
      generate_name: true,
      path: 'Messages',
      tags: ['Messages'],
      file,
      author_quota: this.loggedUserInfo.connection?.custom_quota,
    };

    const savedFile: IAttachmentDocument = {
      ...document,
      status: AttachmentStatus.PENDING_UPLOAD,
      file_id: (Math.random() * 25000).toString(),
    };

    this.updateDataListTableElement(index, savedFile);
    this.toBeUploadedData.push(entity);
    this.toBeUploadedDocuments.push(savedFile);
  }

  getStatusChip(document: IAttachmentDocument): DataListTableChipDataElement {
    let value;
    let chipBackgroundColor;
    switch (document.status) {
      case AttachmentStatus.UPLOADED:
        value = this.translate.instant('messages.document.uploaded');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.UPLOADED;
        break;

      case AttachmentStatus.FAILED:
        value = this.translate.instant('messages.document.failed');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.FAILED;
        break;

      case AttachmentStatus.UPLOADING:
        value = this.translate.instant('messages.document.uploading');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.UPLOADING;
        break;

      case AttachmentStatus.PENDING_DELETE:
        value = this.translate.instant('messages.document.pending-delete');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.PENDING_DELETE;
        break;

      case AttachmentStatus.PENDING_UPLOAD:
        value = this.translate.instant('messages.document.pending-upload');
        chipBackgroundColor = CHIP_BACKGROUND_COLORS.PENDING_UPLOAD;
        break;

      default:
        break;
    }

    return {
      key: this.translate.instant('messages.document.status'),
      value,
      type: DLT_DATA_TYPES.CHIP,
      chipBackgroundColor,
      chipColor: 'white',
    };
  }

  async uploadDocument() {
    let uploadDataLength = this.toBeUploadedData.length;

    let uploadData: IUploadFileToDMS<File>;

    for (uploadData of this.toBeUploadedData) {
      this.isLoading = true;
      const index = this.documentsDataList.length - uploadDataLength--;

      await new Promise<void>((resolve, reject) => {
        this.filesService.uploadFileToDMS(uploadData).subscribe({
          next: (res) => {
            this.isLoading = false;

            const file: IAttachmentDocument = {
              ...document,
              type: res.data.type,
              status: AttachmentStatus.UPLOADED,
              file_id: res.data._id.toString(),
              name: uploadData.file.name,
              uploaded_date: res.data.last_modified_on,
            };

            this.updateDataListTableElement(index, file);
            this.snackbar.success(SUCCESS_TYPES.SAVED, file.name);

            resolve();
          },
          error: () => {
            this.isLoading = false;
            const file: IAttachmentDocument = {
              ...document,
              status: AttachmentStatus.FAILED,
              name: '',
              type: '',
              uploaded_date: undefined,
            };

            this.updateDataListTableElement(index, file);

            this.snackbar.error(
              this.translate.instant('messages.document.upload-failed')
            );
            reject(
              new Error(
                this.translate.instant('messages.document.upload-failed')
              )
            );
          },
        });
      });
    }
  }

  selectIdentity(
    e: IELAsyncAutocompleteElement | IELAsyncAutocompleteElement[]
  ) {
    this.selectedIdentityReceiver = e as IELAsyncAutocompleteElement;
    if (this.selectedIdentityReceiver?.key) {
      setTimeout(() => {
        this.isShowIdentityError = false;
      }, 0);
    } else {
      setTimeout(() => {
        this.isShowIdentityError = true;
        this.isReceiverMarkAsTouched = true;
      }, 0);
    }
  }

  selectRole(e: IELAsyncAutocompleteElement | IELAsyncAutocompleteElement[]) {
    this.selectedRoleReceiver = e as IELAsyncAutocompleteElement;
    if (this.selectedRoleReceiver?.key) {
      setTimeout(() => {
        this.isShowRoleError = false;
      }, 0);
    } else {
      setTimeout(() => {
        this.isShowRoleError = true;
        this.isReceiverMarkAsTouched = true;
      }, 0);
    }
  }

  selectGroup(e: IELAsyncAutocompleteElement | IELAsyncAutocompleteElement[]) {
    this.selectedGroupReceiver = e as IELAsyncAutocompleteElement;
    if (this.selectedGroupReceiver?.key) {
      setTimeout(() => {
        this.isShowGroupError = false;
      }, 0);
    } else {
      setTimeout(() => {
        this.isShowGroupError = true;
        this.isReceiverMarkAsTouched = true;
      }, 0);
    }
  }

  private deleteDocument(index: number, document: IAttachmentDocument) {
    if (document.status === AttachmentStatus.UPLOADING) {
      // should not be able to delete while uploading
      return;
    }

    if (document.status === AttachmentStatus.PENDING_UPLOAD) {
      this.documentsDataList.splice(index, 1);
      this.documents.splice(index, 1);
      return;
    }

    if (document.status === AttachmentStatus.FAILED) {
      // should be able to delete directly without confirmation
      this.documentsDataList.splice(index, 1);
      this.documents.splice(index, 1);
      return;
    }

    if (document.status === AttachmentStatus.UPLOADED) {
      const dialogData = {
        description: this.translate.instant(
          'knowledgebase.document.description'
        ),
      };

      this.getUserConfirmation
        .confirm(CONFIRMATION_TYPES.DELETE, dialogData)
        .then(async (res: boolean) => {
          this.toBeDeletedData.push(document);

          if (res) {
            const savedFile: IAttachmentDocument = {
              ...document,
              status: AttachmentStatus.PENDING_DELETE,
            };

            this.updateDataListTableElement(index, savedFile);

            this.createThreadForm.controls.body.markAsDirty();
          } else {
            this.snackbar.error(
              this.translate.instant('messages.snackbar.cant-delete')
            );
          }
        });
    }
  }

  private downloadDocument(index: number, document: IAttachmentDocument) {
    this.changeLoadingState(index, true);
    this.filesService
      .downloadEntityFromDMS(document.file_id, document.name)
      .then(() => {
        this.changeLoadingState(index, false);
        this.snackbar.success(
          SUCCESS_TYPES.DOWNLOADED,
          this.translate.instant('messages.file')
        );
      })
      .catch(() => {
        this.changeLoadingState(index, false);
        this.snackbar.error(
          this.translate.instant('messages.snackbar.file-download-failed'),
          document.name
        );
      });
  }

  checkValidity() {
    if (
      this.createThreadForm.invalid ||
      (this.ruleType === TO_RULE_TYPE.TO_IDENTITY &&
        !this.selectedIdentityReceiver?.key) ||
      (this.ruleType === TO_RULE_TYPE.TO_ROLE &&
        !this.selectedRoleReceiver?.key) ||
      (this.ruleType === TO_RULE_TYPE.TO_GROUP &&
        !this.selectedGroupReceiver?.key)
    ) {
      this.snackbar.error('Fill all mandatory fields.');
      return false;
    }

    if (
      this.documents.some(
        (document) => document.status === AttachmentStatus.FAILED
      )
    ) {
      // if there are documents which has not uploaded successfully then show an error
      this.snackbar.error(
        this.translate.instant('messages.document.failed-uploads-exists')
      );
      return false;
    }

    return true;
  }

  async uploadFilesToDMS() {
    this.isLoading = true;

    if (this.toBeUploadedData.length) await this.uploadDocument();
    if (this.toBeDeletedData.length) await this.deleteFiles();
    this.isLoading = false;
  }

  async deleteFiles() {
    this.documentsDataList
      .map((obj, index) =>
        obj.data.some(
          (entry: { key: string; value: string }) =>
            entry.key === TYPES.STATUS &&
            entry.value === DOCUMENT_DATA_STATUS.PENDING_DELETE
        )
          ? this.removeFromDataListTable(index)
          : -1
      )
      .filter((index) => index !== -1);

    if (this.toBeDeletedData.length) {
      this.toBeDeletedData.forEach(async (data) => {
        const index = this.documents.findIndex((item) => item === data);
        await new Promise<void>((resolve, reject) => {
          this.filesService.deleteEntityFromDMS(data.file_id).subscribe({
            next: () => {
              this.documents.splice(index, 1);
              this.isLoading = false;
              resolve();
            },
            error: (e) => {
              this.isLoading = false;
              reject(e);
            },
          });
        });
      });
    }
  }

  async send() {
    this.isReceiverMarkAsTouched = true;
    this.createThreadForm.markAllAsTouched();

    if (this.checkValidity()) {
      await this.uploadFilesToDMS();

      const createData: IMessage = {
        message: this.createThreadForm.controls.message.value,
        priority: this.createThreadForm.controls.priority.value,
        receiver: {
          ...(this.ruleType === TO_RULE_TYPE.TO_IDENTITY
            ? { identity_id: this.selectedIdentityReceiver?.key?.toString() }
            : {}),
          ...(this.ruleType === TO_RULE_TYPE.TO_ROLE
            ? { role_id: this.selectedRoleReceiver?.key?.toString() }
            : {}),
          ...(this.ruleType === TO_RULE_TYPE.TO_GROUP
            ? { group_id: this.selectedGroupReceiver?.key?.toString() }
            : {}),
        },
        status: MESSAGE_STATUS.SENDER_REPLIED,
        subject: this.createThreadForm.controls.subject.value,
        attachments: this.documents,
      };
      this.dialogRef.close(createData);
    }
  }

  changeLoadingState(index: number, is_loading: boolean) {
    this.documentsDataList[index].loading = is_loading;
    this.documentsDataList[index].expanded = !is_loading;
  }

  action(e: { action: string; data: IAttachmentDocument; index: number }) {
    if (e.action === ACTIONS.DELETE) {
      this.deleteDocument(e.index, e.data);
    } else if (e.action === ACTIONS.DOWNLOAD) {
      this.downloadDocument(e.index, e.data);
    }
  }

  addToDataListTable(document: IAttachmentDocument) {
    const dataElements: DataListTableDataElement[] = [];
    const actionElements: DataListTableActionElement[] = [];

    dataElements.push(
      {
        key: this.translate.instant('messages.name'),
        value: document.name,
      },
      this.getStatusChip(document),
      {
        key: this.translate.instant('messages.date'),
        value: this.dateTimeFormatService.formatDateTime(
          document.uploaded_date
        ),
      }
    );

    actionElements.push(this.createDownloadAction(document));
    actionElements.push(this.createDeleteAction(document));

    this.documentsDataList.push({
      loading: document.status === AttachmentStatus.UPLOADING,
      expanded: document.status === AttachmentStatus.FAILED,
      data: dataElements,
      icon: 'insert_drive_file',
      actions: actionElements,
      index: this.documentsDataList.length,
    });
    this.documents.push(document);
  }

  async updateDataListTableElement(
    index: number,
    document: IAttachmentDocument
  ) {
    const dataElements: DataListTableDataElement[] = [];
    const actionElements: DataListTableActionElement[] = [];

    dataElements.push(
      {
        key: this.translate.instant('messages.name'),
        value: document.name,
      },
      this.getStatusChip(document),
      {
        key: this.translate.instant('messages.date'),
        value: this.dateTimeFormatService.formatDateTime(
          document.uploaded_date
        ),
      }
    );

    actionElements.push(this.createDownloadAction(document));
    actionElements.push(this.createDeleteAction(document));

    this.documentsDataList[index] = {
      loading: document.status === AttachmentStatus.UPLOADING,
      expanded: document.status === AttachmentStatus.FAILED,
      data: dataElements,
      icon: 'insert_drive_file',
      actions: actionElements,
      index,
    };

    this.documents[index] = document;
  }

  removeFromDataListTable(index: number) {
    this.documentsDataList.splice(index, 1);
    this.documents.splice(index, 1);
    this.documentsDataList.forEach((element, index) => {
      element.index = index;
    });
  }

  public createDeleteAction(
    data: IAttachmentDocument
  ): DataListTableActionElement {
    return {
      icon: 'delete',
      face: '',
      tooltip: this.translate.instant('messages.delete'),
      action: ACTIONS.DELETE,
      data,
    };
  }

  public createDownloadAction(
    document: IAttachmentDocument
  ): DataListTableActionElement {
    return {
      icon: 'save_alt',
      face: this.translate.instant('messages.download'),
      tooltip: this.translate.instant('messages.download'),
      action: ACTIONS.DOWNLOAD,
      data: document,
      disabled: document.status !== AttachmentStatus.PENDING_UPLOAD,
    };
  }

  async generateAvatars(identity: IIdentity): Promise<string> {
    return await this.filesService.getProfilePictureOneTime(identity);
  }

  onFilterMembers(eventData: IFilterData) {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('name', eventData.event);
    httpParams = httpParams.append('receiving_party', this.ruleType);
    httpParams = httpParams.append('logged_user_id', this.loggedUserInfo._id);
    httpParams = httpParams.append('start', eventData.start);
    httpParams = httpParams.append('size', eventData.size);

    this.messageRuleService.getFilteredRuleUsers(httpParams).subscribe({
      next: async (res) => {
        const filterMembersList: IELAsyncAutocompleteElement[] = [];
        const connections = res.data as IConnection[];
        const roles = res.data as IRole[];
        const groups = res.data as MessageGroupResponseDTO[];

        if (this.ruleType === TO_RULE_TYPE.TO_IDENTITY) {
          await Promise.all(
            connections.map(async (item) => {
              filterMembersList.push({
                key: (item.identity as IIdentity)._id.toString(),
                value: `${item.role_name}::${
                  (item.identity as IIdentity).first_name
                } ${(item.identity as IIdentity)?.last_name ?? ''}`,
                avatar: await this.generateAvatars(item?.identity as IIdentity),
              });
            })
          );
        } else if (this.ruleType === TO_RULE_TYPE.TO_ROLE) {
          roles.forEach((item) => {
            filterMembersList.push({
              key: item._id.toString(),
              value: item.role,
            });
          });
        } else {
          groups.forEach((item) => {
            filterMembersList.push({
              key: item._id.toString(),
              value: item.group_name,
            });
          });
        }
        this.totalUsers = res.count;
        this.usersListForAsyncAutoComplete =
          eventData.type === 'scroll'
            ? this.usersListForAsyncAutoComplete.concat(filterMembersList)
            : filterMembersList;
      },
    });
  }
}
